import React, { useState } from 'react';

import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';

import { getRegionNamesAndCode } from '@luxuryescapes/lib-regions';

import { CreateTagInput } from '~/services/TagsService';
import { formatDateLongISO } from '~/services/TimeService';

import type { AssignmentFormData } from './TagAssignments/TagAssignments';
import TagForm, { type FormTag } from './TagForm';
import { TAG_TYPE_BASIC, isContentTag, isScheduleFreeTag } from './constants';
import { useCreateTag } from './hooks';

export default function TagCreate({ onBack }: { onBack: () => void }) {
  const [assignments, setAssignments] = useState<Array<AssignmentFormData>>([]);
  const { enqueueSnackbar } = useSnackbar();

  const createTag = useCreateTag();

  const form = useForm<FormTag>({
    defaultValues: {
      name: '',
      tagType: TAG_TYPE_BASIC,
      schedules: [],
    },
  });

  const onSubmit = async (data: FormTag) => {
    if (assignments.length === 0) {
      enqueueSnackbar('Please add at least one assignment before creating the tag', { variant: 'error' });
      return;
    }

    const scheduleFree = isScheduleFreeTag(data.tagType);
    const schedules = scheduleFree
      ? []
      : data.schedules.length > 0
        ? data.schedules
        : [
            {
              brands: ['luxuryescapes'],
              regions: getRegionNamesAndCode().map((r) => r.code),
              activeStartDate: formatDateLongISO(new Date()),
              activeEndDate: formatDateLongISO(new Date(new Date().setFullYear(new Date().getFullYear() + 100))),
            },
          ];

    const createTagData: CreateTagInput = {
      name: data.name,
      tagType: data.tagType,
      // svc-tag marks only content tags permanent. Basic is schedule-free and stays non-permanent.
      permanent: isContentTag(data.tagType),
      assignments: assignments.map((assignment) => ({
        entityType: assignment.entityType,
        entityId: assignment.entityId,
      })),
      schedules: schedules.map((schedule) => ({
        brands: schedule.brands,
        regions: schedule.regions,
        activeStartDate: schedule.activeStartDate,
        activeEndDate: schedule.activeEndDate,
        travelStartDate: schedule.travelStartDate || undefined,
        travelEndDate: schedule.travelEndDate || undefined,
      })),
    };
    try {
      await createTag.mutateAsync(createTagData);
    } catch {
      // useCreateTag already surfaces the error and leaves the draft in place.
      return;
    }

    onBack();
  };

  const handleSave = form.handleSubmit(onSubmit, (errors) => {
    enqueueSnackbar(errors['schedules']?.message || 'Unknown error', { variant: 'error' });
  });

  return (
    <FormProvider {...form}>
      <TagForm
        mode="create"
        control={form.control}
        isSaving={createTag.isPending}
        assignments={assignments}
        onAssignmentsChange={setAssignments}
        onBack={onBack}
        onSave={handleSave}
      />
    </FormProvider>
  );
}
